import React, { Fragment } from "react";
import PropTypes from "prop-types";

import "./CustomProjectPage.css";
import {
  PROJECT_INBOX,
  PROJECT_NOTIFICATIONS,
  PROJECT_BOOKMARKS,
  PROJECT_REWARDS,
  PROJECT_LEADERBOARD,
  PROJECT_ACTIVITY,
  PROJECT_FRAME,
  CHALLENGE,
  TOPIC,
  TOPIC_CATEGORY,
  PROJECT_TEAMS,
  PROJECT_USERS
} from "App/Routes";
import Link from "components/shared/Link/Link";
import CustomTopicCard from "./CustomTopicCard";
import ProjectPage from "components/Project/ProjectPage";
import CustomDesktopProjectPage from "./CustomDesktopProjectPage";
import CustomMobileProjectPage from "./CustomMobileProjectPage";

import { ENABLE_INBOX, ENABLE_NOTIFICATIONS_PAGES } from "config";
import sessionStorageService from "services/sessionStorageService";
import localize from "lang/localize";

const propTypes = {
  sessionKey: PropTypes.string,
  projectId: PropTypes.number.isRequired /* Project ID */,
  project: PropTypes.object.isRequired,
  topics: PropTypes.array.isRequired,
  more: PropTypes.bool,
  buttons: PropTypes.array,
  notifications: PropTypes.number.isRequired,
  inboxUnread: PropTypes.number.isRequired,
  user: PropTypes.object,
  handleMore: PropTypes.func,
  language: PropTypes.string
};

class CustomProjectPage extends ProjectPage {
  constructor() {
    super();
    this.state = {
      randomNumber: Math.floor(Math.random() * 3)
    };
    this.renderTopics = this.renderTopics.bind(this);
    this.renderProjectPanel = this.renderProjectPanel.bind(this);
    this.renderProjectUserPanel = this.renderProjectUserPanel.bind(this);
  }

  renderProjectPanel() {
    const PROJECT_IMAGE =
      this.props.project && this.props.project.bannerImage
        ? this.props.project.bannerImage
        : "";
    const PROJECT_TITLE =
      this.props.project && this.props.project.title
        ? this.props.project.title
        : "";
    const PROJECT_DESCRIPTION =
      this.props.project && this.props.project.description
        ? this.props.project.description
        : "";

    return (
      <div className="project-info-panel fullwidth verticalpadding horizontalpadding flex align-items-center">
        <div className="pure-g fullwidth">
          <div className="pure-u-9-24 hide-from-lg" />
          <div className="pure-u-6-24 pure-u-lg-6-24 on-lg-rightpadding-10 flex align-items-center">
            <span className="square-image-wrapper fullwidth below-lg-bottommargin-10">
              <span className="square-image circle">
                <img src={PROJECT_IMAGE} alt={PROJECT_TITLE} />
              </span>
            </span>
          </div>
          <div className="pure-u-9-24 hide-from-lg" />
          <div className="pure-u-1-1 pure-u-lg-18-24 on-lg-leftpadding-10 below-lg-text-align-center">
            <h5>{PROJECT_TITLE}</h5>
            <p className="nomargin">{PROJECT_DESCRIPTION}</p>
          </div>
        </div>
      </div>
    );
  }

  renderProjectInboxIcon() {
    if (ENABLE_INBOX && this.props.user && this.props.user.id) {
      return (
        <div className="pure-u-6-24 pure-u-sm-4-24 pure-u-md-4-24 pure-u-lg-3-24">
          <Link
            to={PROJECT_INBOX.format(this.props.projectId)}
            className="mobile-project-profile-button mobile-project-profile-button-border fullwidth"
          >
            <i className="fas fa-comments" aria-hidden="true" />
            {this.props.inboxUnread && this.props.inboxUnread > 0 ? (
              <div className="notification-dot" id="unreadInboxDot" />
            ) : null}
          </Link>
        </div>
      );
    } else {
      return null;
    }
  }

  renderProjectNotificationIcon() {
    if (this.props.user && this.props.user.id && ENABLE_NOTIFICATIONS_PAGES) {
      return (
        <div className="pure-u-6-24 pure-u-sm-4-24 pure-u-md-4-24 pure-u-lg-3-24">
          <Link
            to={PROJECT_NOTIFICATIONS.format(this.props.projectId)}
            className="mobile-project-profile-button mobile-project-profile-button-border"
          >
            <i className="fas fa-bell" aria-hidden="true" />
            {this.props.notifications && this.props.notifications > 0 ? (
              <div className="notification-dot" id="unreadNotificationDot" />
            ) : null}
          </Link>
        </div>
      );
    } else {
      return null;
    }
  }

  renderProjectUserButtons() {
    if (
      this.props.buttons &&
      Array.isArray(this.props.buttons) &&
      this.props.buttons.length > 0
    ) {
      return this.props.buttons.map((button, index) => {
        const IS_SECOND_ITEM_IN_2_COL_GRID = index % 2 === 1;

        switch (button.buttonType) {
          case "bookmarks":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_BOOKMARKS.format(this.props.projectId)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-bookmark" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "rewards":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_REWARDS.format(this.props.projectId)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-gift" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "leaderboard":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_LEADERBOARD.format(this.props.projectId)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-trophy" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "activity":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_ACTIVITY.format(this.props.projectId)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-list-alt" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "web-url":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <a
                  href={button.objectReference}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link flex fullwidth align-items-center"
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-external-link-alt" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </a>
              </div>
            );
          case "web-url-session":
            const ENCODED_LINK = encodeURIComponent(button.objectReference);
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_FRAME.format(this.props.projectId, ENCODED_LINK)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-link" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "challenge-profile":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex align-items-center"
                  to={CHALLENGE.format(button.objectReference)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="gamicons icon-challenge-placeholder" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "topic":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={TOPIC.format(button.objectReference)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="gamicons icon-topics" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "topic-category":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={TOPIC_CATEGORY.format(
                    this.props.projectId,
                    button.objectReference
                  )}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-layer-group" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "teams":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_TEAMS.format(this.props.projectId)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-users" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          case "players":
            return (
              <div
                className={
                  "project-user-button-container flex pure-u-1-1 pure-u-md-1-2 bottompadding-5" +
                  (IS_SECOND_ITEM_IN_2_COL_GRID
                    ? " on-md-leftpadding-5"
                    : " on-md-rightpadding-5")
                }
                key={button.id}
              >
                <Link
                  className="flex fullwidth align-items-center"
                  to={PROJECT_USERS.format(this.props.projectId)}
                >
                  <div className="pure-u-5-24 rightpadding-5">
                    <div className="flex justify-content-center">
                      <i className="fas fa-user" />
                    </div>
                  </div>
                  <div className="pure-u-19-24">{button.buttonText}</div>
                </Link>
              </div>
            );
          default:
            return null;
        }
      });
    } else {
      return null;
    }
  }

  renderUserProfileSection() {
    const USER_IMAGE =
      this.props.user && this.props.user.photoLarge
        ? this.props.user.photoLarge
        : "";
    const USER_NAME =
      this.props.user && this.props.user.name ? this.props.user.name : "";
    const USER_POINTS =
      this.props.user && typeof this.props.user.points === "number"
        ? this.props.user.points
        : "";

    return (
      <div className="project-user-profile-section fullwidth flex-from-md align-items-center justify-content-center bottommargin-10">
        <div className="project-user-profile-image-container on-md-rightpadding-5 flex-below-md justify-content-center">
          <span className="square-image-wrapper fullwidth below-md-bottommargin-10">
            <span className="square-image circle">
              <img src={USER_IMAGE} alt={USER_NAME} />
            </span>
          </span>
        </div>
        <div className="project-user-profile-text-container on-md-leftpadding-5 below-md-text-align-center">
          <p className="bottommargin-5 below-md-bottommargin-10">
            <strong>{USER_NAME}</strong>
          </p>
          <p
            className="project-user-panel-points-text nomargin hide-below-md"
            dangerouslySetInnerHTML={{
              __html: localize("points_text", this.props.language).format(
                "<strong>" + USER_POINTS + "</strong>"
              )
            }}
          />
          <p className="project-user-panel-points-text bottommargin-5 hide-from-md">
            <strong>{USER_POINTS}</strong>
          </p>
          <p className="nomargin hide-from-md">
            {localize("points_just_text", this.props.language)}
          </p>
        </div>
      </div>
    );
  }

  renderProjectUserPanel() {
    return (
      <div className="project-user-panel flex align-items-center verticalpadding horizontalpadding">
        <div className="fullwidth pure-g">
          <div className="flex-below-md align-items-center on-md-bottompadding-20 on-md-border-bottom-gray border-thin pure-u-1-2 pure-u-md-1-1 below-md-rightpadding-5">
            <div className="fullwidth">
              {this.renderUserProfileSection()}
              <div className="pure-g justify-content-center">
                {this.renderProjectInboxIcon()}
                {this.renderProjectNotificationIcon()}
              </div>
            </div>
          </div>
          <div className="on-md-toppadding-20 pure-u-1-2 pure-u-md-1-1 flex-below-md align-items-center below-md-leftpadding-5">
            <div className="pure-g">{this.renderProjectUserButtons()}</div>
          </div>
        </div>
      </div>
    );
  }

  /* Custom:
  /*
    isMobile bool prop determines if these Topic cards
    are rendered in mobile or desktop view: for
    ScrollableAnchor library
  */
  renderTopics(topics, isMobile) {
    return topics.map((topic, index) => (
      <Fragment key={topic.id}>
        <div className="pure-u-1 pure-u-md-4-24">
          <CustomTopicCard
            sessionKey={this.props.sessionKey}
            projectId={this.props.projectId}
            isMobile={isMobile}
            allowReset={topic.allowReset || false}
            title={topic.title}
            description={topic.description}
            totalChallenges={topic.challengeNo}
            completedChallenges={topic.userChallengeCompletedNo || 0}
            img={topic.bannerImage}
            id={topic.id}
            started={topic.joined || false}
            locked={this.getTopicLock(topic.unlockable, topic.locked)}
            expired={typeof topic.expired === "boolean" ? topic.expired : false}
            completed={
              topic.challengeNo === topic.userChallengeCompletedNo || false
            }
            points={topic.points || 0}
            language={this.props.language}
          />
        </div>
        {/* render on indexes 0, 1, 2, 3 */}
        {index % 5 !== 4 ? (
          <div className="hide-below-md pure-u-md-1-24" />
        ) : null}
      </Fragment>
    ));
  }

  render() {
    const IS_640_DESKTOP_PROJECT =
      sessionStorageService.getItem("640_desktop_project") === "true"
        ? true
        : false;

    return (
      <div className="project custom-project container nopadding box-sizing-border-box-all">
        {this.props.showPopup && this.renderPopupAlertDialog()}
        {this.props.showShareGameDialog && this.renderShareGameDialog()}
        {this.props.showTopicsListDialog && this.renderTopicsListDialog()}
        <div
          className={
            "desktop-project verticalpadding horizontalpadding pure-g" +
            (IS_640_DESKTOP_PROJECT ? " hide-below-md-640" : " hide-below-md")
          }
        >
          {/* Render desktop Project page here */}
          <CustomDesktopProjectPage
            topics={this.props.topics}
            more={this.props.more}
            renderProjectPanel={this.renderProjectPanel}
            renderProjectUserPanel={this.renderProjectUserPanel}
            renderTopics={this.renderTopics}
            handleMore={this.props.handleMore}
          />
        </div>
        <div
          className={
            "verticalpadding horizontalpadding " +
            (IS_640_DESKTOP_PROJECT ? "hide-from-md-640" : "hide-from-md")
          }
        >
          {/* Render mobile Project page here */}
          <CustomMobileProjectPage
            topics={this.props.topics}
            more={this.props.more}
            renderProjectPanel={this.renderProjectPanel}
            renderProjectUserPanel={this.renderProjectUserPanel}
            renderTopics={this.renderTopics}
            handleMore={this.props.handleMore}
          />
        </div>
      </div>
    );
  }
}

CustomProjectPage.propTypes = propTypes;

export default CustomProjectPage;
